import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "semantic-ui-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import { selectColumn } from "components/BaseTableV3/selectColumn";
import { TableLink } from "components/BaseTableV3/TableLink";
import CustomDropdown from "components/CustomDropdown";
import TagList from "components/TagList";
import { formatDate } from "components/helpers";
import useAuthorized from "hooks/useAuthorized";

import TextCell from "routes/custom_tables/grid/cells/TextCell";
import NumberCell from "routes/custom_tables/grid/cells/NumberCell";
import DateCell from "routes/custom_tables/grid/cells/DateCell";
import BooleanCell from "routes/custom_tables/grid/cells/BooleanCell";
import SingleSelectCell from "routes/custom_tables/grid/cells/SingleSelectCell";
import MultiSelectCell from "routes/custom_tables/grid/cells/MultiSelectCell";
import ListCell from "routes/custom_tables/grid/cells/ListCell";
import RelationshipCell from "routes/custom_tables/grid/cells/RelationshipCell";
import EmailCell from "routes/custom_tables/grid/cells/EmailCell";
import UrlCell from "routes/custom_tables/grid/cells/UrlCell";
import PhoneCell from "routes/custom_tables/grid/cells/PhoneCell";
import PercentageCell from "routes/custom_tables/grid/cells/PercentageCell";
import CurrencyCell from "routes/custom_tables/grid/cells/CurrencyCell";

const CELL_BY_TYPE = {
  short_text: TextCell,
  text: TextCell,
  number: NumberCell,
  date: DateCell,
  boolean: BooleanCell,
  single_select: SingleSelectCell,
  multi_select: MultiSelectCell,
  list: ListCell,
  relationship: RelationshipCell,
  email: EmailCell,
  url: UrlCell,
  phone: PhoneCell,
  percentage: PercentageCell,
  currency: CurrencyCell,
};

// Only btree-indexed scalars are sortable. Array columns have no backing
// index for ordering, and long text is content-only — both fall back to
// `id` on the server, so the grid hides their sort handle to match.
const UNSORTABLE_TYPES = new Set(["multi_select", "list", "text"]);

// The ID is the row's link to its detail page, matching the other tables
// (Contacts/Entities/Cases) — the row itself is not clickable.
const buildIdColumn = ({ slug }) => ({
  id: "id",
  accessorKey: "id",
  header: "ID",
  size: 80,
  meta: { isDisplay: true },
  cell: ({ row }) => (
    <TableLink href={`/custom_tables/${slug}/${row.original?.id}`}>
      {row.original?.id ?? null}
    </TableLink>
  ),
});

const renderTimestamp = value =>
  value == null || value === "" ? null : formatDate(value);

const renderActor = actor => actor?.full_name ?? null;

const createdAtColumn = {
  id: "created_at",
  accessorKey: "created_at",
  header: "Created at",
  size: 160,
  meta: { isDisplay: true },
  cell: ({ row }) => renderTimestamp(row.original?.created_at),
};

const updatedAtColumn = {
  id: "updated_at",
  accessorKey: "updated_at",
  header: "Updated at",
  size: 160,
  meta: { isDisplay: true },
  cell: ({ row }) => renderTimestamp(row.original?.updated_at),
};

const createdByColumn = {
  id: "created_by",
  accessorKey: "created_by",
  header: "Created by",
  size: 160,
  meta: { isDisplay: true },
  cell: ({ row }) => renderActor(row.original?.created_by),
};

const updatedByColumn = {
  id: "updated_by",
  accessorKey: "updated_by",
  header: "Updated by",
  size: 160,
  meta: { isDisplay: true },
  cell: ({ row }) => renderActor(row.original?.updated_by),
};

// Tags are multi-valued, so the column isn't sortable (mirrors the
// ContactsTable / RevenueOpportunities Tags pattern). `onUpdate` refetches
// the rows so an inline tag edit reflects without a manual refresh.
const buildTagsColumn = ({ slug, onUpdate }) => ({
  id: "tags",
  accessorKey: "tags",
  header: "Tags",
  size: 220,
  enableSorting: false,
  meta: { isDisplay: true },
  cell: ({ row }) => (
    <TagList
      tags={row.original?.tags}
      modelType={`CustomTable:${slug}`}
      modelId={row.original?.id}
      onUpdate={onUpdate}
      tableCell
    />
  ),
});

const RowActionsMenu = ({ row, slug, onDeleteRow, canEdit, canDelete }) => {
  const navigate = useNavigate();
  if (!canEdit && !canDelete) return null;
  return (
    <span
      role="button"
      tabIndex={-1}
      aria-label="Row actions"
      onClick={e => e.stopPropagation()}
    >
      <CustomDropdown icon="ellipsis horizontal">
        <Dropdown.Menu direction="left">
          {canEdit && (
            <Dropdown.Item
              icon="pencil"
              text="Edit"
              onClick={() => navigate(`/custom_tables/${slug}/${row.id}/edit`)}
            />
          )}
          {canDelete && (
            <Dropdown.Item
              icon="trash"
              text="Delete"
              onClick={() => onDeleteRow?.(row)}
            />
          )}
        </Dropdown.Menu>
      </CustomDropdown>
    </span>
  );
};

const buildActionsColumn = ({ slug, onDeleteRow, canEdit, canDelete }) => ({
  id: "actions",
  size: 60,
  enableSorting: false,
  enableResizing: false,
  meta: { isDisplay: true },
  header: () => null,
  cell: ({ row }) => (
    <RowActionsMenu
      row={row.original}
      slug={slug}
      onDeleteRow={onDeleteRow}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  ),
});

const buildSchemaColumn = ({ column }) => {
  const Cell = CELL_BY_TYPE[column.type] ?? TextCell;
  return {
    id: column.field_id,
    accessorKey: column.field_id,
    header: column.display_name,
    ...(UNSORTABLE_TYPES.has(column.type) ? { enableSorting: false } : {}),
    cell: ({ row }) => (
      <Cell
        value={row.original?.[column.field_id]}
        column={column}
        display={row.original?.[`${column.field_id}__display`]}
      />
    ),
  };
};

export const useDynamicColumns = (schema, { onDeleteRow, onUpdate } = {}) => {
  const pathCanWrite = useAuthorized([ACL_RELATIONSHIPS.customTable.writeData]);
  const pathCanDelete = useAuthorized([
    ACL_RELATIONSHIPS.customTable.deleteData,
  ]);
  return useMemo(() => {
    const slug = schema?.slug;
    // Per-table grants on the schema DTO are authoritative; fall back to the
    // coarse ACL only when the DTO omits them.
    const perTableCan = schema?.current_user_can ?? null;
    const canEdit = perTableCan ? perTableCan.write : pathCanWrite;
    const canDelete = perTableCan ? perTableCan.delete : pathCanDelete;
    const systemColumns = [
      selectColumn,
      buildIdColumn({ slug }),
      createdAtColumn,
      updatedAtColumn,
      createdByColumn,
      updatedByColumn,
    ];
    // A table whose admin named a column ``tags`` owns that key, so the system
    // Tags column steps aside (the schema column renders it instead).
    const hasTagsColumn = (schema?.columns ?? []).some(
      c => c.field_id === "tags"
    );
    if (!hasTagsColumn) {
      systemColumns.push(buildTagsColumn({ slug, onUpdate }));
    }
    if (!schema || !Array.isArray(schema.columns)) {
      return [
        ...systemColumns,
        buildActionsColumn({ slug, onDeleteRow, canEdit, canDelete }),
      ];
    }
    const dataColumns = schema.columns.map(column =>
      buildSchemaColumn({ column })
    );
    return [
      ...systemColumns,
      ...dataColumns,
      buildActionsColumn({ slug, onDeleteRow, canEdit, canDelete }),
    ];
  }, [schema, onDeleteRow, onUpdate, pathCanWrite, pathCanDelete]);
};

export default useDynamicColumns;
